/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties, ReactNode} from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import AxisX from "./AxisX";
import AxisY from "./AxisY";

import {COLORS} from "@res/colors";
import {AxesLabels, Coordinates, D3TableData, Frames, GraphsModule} from "./GraphsModule";
import {ScaleLinear} from "d3-scale";
import {Day, Week} from "@intuitionrobotics/ts-common";

export type TableData = D3TableData & {
    icon?: ReactNode
    groupId?: string
}

export type ExtraData = {
    y: number,
    label: string,
    text?: string,
    style?: CSSProperties
}

export type Sizing = {
    height: number,
    width?: number
}

type Props = {
    frames?: Frames
    axesLabels?: AxesLabels,
    data: TableData[],
    viewBox: number,
    frequency: number,
    sizing: Sizing,
    timeframe: number,
    disabled?: number[],
    extraData?: ExtraData[],
    displayedAsTable?: boolean,
    endDate?: number
    rowHeight?: number
    linesToDraw?: number[]
    title?: string
}


type State = {
    currentEndDate: number
    scrolling: boolean
    scrollPosition: number
};

export class ContinuousTableGraph
    extends BaseComponent<Props, { currentEndDate: number }> {

    // __onPatientChange() {
    // 	this.setState({currentEndDate: GraphsModule.tonightAtMidnight});
    // }
    private graphRef?: HTMLDivElement;

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.data !== prevProps.data)
            this.setState({
                currentEndDate: this.props.endDate || GraphsModule.getEndDate()
            });
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            currentEndDate: this.props.endDate || GraphsModule.getEndDate()
        };
    }

    margin = {
        top: 20,
        bottom: 40,
        left: 0,
        right: 0
    };

    private width = (): number => {
        const graphRef = this.graphRef;
        const width = this.props.sizing.width || graphRef?.clientWidth;
        if (!width)
            return 0;

        return width - this.margin.right - this.margin.left;
    };
    height = () => this.props.sizing.height - this.margin.top - this.margin.bottom;

    private minAndMax = () => {
        const arrayOfProps: Coordinates[] = this.props.data.map(_lineData => {
            return _lineData.data;
        });
        return GraphsModule.extent(arrayOfProps, this.props.frames);
    };

    private maxX = this.minAndMax().maxX;
    private maxY = this.minAndMax().maxY;

    xScale = (width: number) => {
        return GraphsModule.scale(this.state.currentEndDate - (this.props.frequency * this.props.viewBox), this.state.currentEndDate, 0, width);
    };

    yScale = (height: number) => {
        const num = this.minAndMax().maxY;
        return this.props.displayedAsTable ? GraphsModule.scale(this.state.currentEndDate - (Day * this.props.viewBox), this.state.currentEndDate, height, 0) :
            GraphsModule.yScale(0, num + 1, height);
    };


    dataPoints = (data: TableData[], xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>) => {
        // const xScale = this.xScale();
        // const yScale = this.yScale();
        if (!data || data.length === 0)
            return;

        const foreignObjectHeight = 40;
        const maxY = this.minAndMax().maxY;
        const yShift = maxY === 1 ? (this.props.sizing.height / 3) - foreignObjectHeight / 2 :
            // TODO: Fix this function, it should be this.minAndMax().maxY === 0, but fails due to precision error
            maxY < 0.1 ? this.props.sizing.height / 2 - foreignObjectHeight / 2
                : ((this.props.sizing.height / this.maxY) / 2);

        return data.map((d, i) => {
            return <foreignObject
                key={i}
                width="60" height={foreignObjectHeight}
                style={{overflow: 'visible'}}
                //width of foreign object - width of image
                x={xScale(d.data.x - (this.props.frequency / 2))}
                y={yScale(d.data.y) - (this.props.rowHeight ? ((this.props.rowHeight + foreignObjectHeight) / 2) : yShift)}
            >
                {d.icon}
            </foreignObject>;

        });
    };

    disabled = (yScale: ScaleLinear<number, number, any>, width: number) => {
        const disabled = this.props.disabled;
        if (!disabled)
            return;
        return this.props.disabled?.map(_row => {
            const lines = [];
            let x = -50;
            const y1 = yScale(_row + 1);
            const y2 = yScale(_row + 1) + (yScale(_row) - yScale(_row + 1));
            for (let i = 0; i < width / 9; i++) {
                const line = <line
                    style={{
                        stroke: COLORS.grays_2, strokeWidth: 1
                    }}
                    y1={y1}
                    y2={y2}
                    x1={x}
                    x2={x + 50}
                    key={i}
                />;
                lines.push(
                    line
                );
                x += 10;
            }
            return lines;
        });

    };


    getAxesDiv = (axesNumbers: number[]) => {
        const axesLabels = GraphsModule.getAxesLabels(axesNumbers, this.props.frequency);
        return axesLabels.map((_label, i) => {
            const dateArray = _label.split(',');
            return <div key={i}>
                <div style={{fontSize: 11, color: COLORS.steelGray, textAlign: "center", fontFamily: "Inter-Regular"}}>{dateArray[0]}</div>
                <div style={{fontSize: 11, color: COLORS.steelGray}}>{dateArray[1]}</div>
            </div>;
        });
    };


    getAxesDates = () => {
        const axesNumbers = [];
        let x = this.state.currentEndDate;
        const endDate = this.props.frequency === Day ? this.props.viewBox : this.props.viewBox * 7;
        for (let i = 0; i < endDate; ++i) {
            axesNumbers.unshift(x);
            x -= Day;
        }
        return axesNumbers;
    };

    renderXAndY = (width: number, height: number) => {
        const range = this.props.frequency * this.props.viewBox;
        const ticks = this.props.displayedAsTable ? this.props.timeframe : this.maxY + 1;
        const axisPoint = this.props.displayedAsTable ? 0 : (this.state.currentEndDate - range) + (this.props.frequency / 2);
        const axesDates = this.getAxesDates();
        return <>
            <AxisY
                yScale={this.yScale(height)}
                width={width}
                ticks={ticks}
                tickValues={this.props.axesLabels?.y}
                tickLines={true}
                placeInMiddle={true}
                tickArray={this.props.displayedAsTable ? axesDates : undefined}
                label={true}
                tickSpecificLines={this.props.linesToDraw}
            />

            <AxisX xScale={this.xScale(width)}
                   frequency={this.props.frequency}
                   height={height}
                   tickValues={this.props.displayedAsTable ? undefined : this.getAxesDiv(axesDates)}
                   axisPoint={axisPoint}
                   minValue={this.props.displayedAsTable ? this.props.frequency / 2 : this.state.currentEndDate - range}
                   viewBox={this.props.displayedAsTable ? this.maxX : this.props.viewBox}
                   axisLine={this.props.displayedAsTable}
                   shiftData={this.props.frequency === Week ? 3 * Day : - Day / 2}
                   tickLines={this.props.displayedAsTable}
                //this is a hack, this should be a ref for the size of the tick div
                   alignMiddle={this.props.frequency !== Day}
            />
        </>;
    };

    render() {
        const width = this.width();
        const height = this.height();
        const yScale = this.yScale(height);
        const xScale = this.xScale(width);
        return <div ref={ref => {
            if (!ref || this.graphRef)
                return;

            this.graphRef = ref;
            this.forceUpdate();
        }}>
            <svg width={width} height={this.props.sizing.height} style={{overflow: this.props.extraData ? 'visible' : 'auto'}}>
                <g transform={`translate(${-20},${0})`} width={this.props.sizing.width} style={{overflow: 'hidden'}}>
                    {this.renderXAndY(width, height)}
                    {this.dataPoints(this.props.data, xScale, yScale)}
                    {this.props.disabled && <>{this.disabled(yScale, width)}</>}
                </g>
            </svg>
        </div>;
    }

}


export default ContinuousTableGraph;
